import { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Camera, Receipt } from 'lucide-react';
import API from '@/utils/api';
import { toast } from 'sonner';

const AddReimbursementDialog = ({ open, onOpenChange, onReimbursementAdded }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    receipt_image: null,
    notes: '',
    status: 'outstanding',
  });
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large (max 5MB)');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setFormData(prev => ({ ...prev, receipt_image: event.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Description is required');
      return;
    }
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Valid amount is required');
      return;
    }
    
    setLoading(true);
    try {
      await API.post('/reimbursements', {
        ...formData,
        description: formData.description.trim(),
        amount: parseFloat(formData.amount),
      });
      onReimbursementAdded();
      setFormData({
        description: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        receipt_image: null,
        notes: '',
        status: 'outstanding',
      });
    } catch (error) {
      console.error('Failed to add reimbursement:', error);
      toast.error('Failed to add reimbursement');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Reimbursement</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Receipt Image */}
          <div className="space-y-2">
            <Label>Receipt Image</Label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileSelect}
              className="hidden"
            />
            
            {formData.receipt_image ? (
              <div className="relative rounded-lg overflow-hidden border border-slate-200">
                <img 
                  src={formData.receipt_image} 
                  alt="Receipt" 
                  className="w-full h-32 object-cover"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute bottom-2 right-2 bg-white/80"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Camera className="h-4 w-4 mr-1" />
                  Change
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="w-full h-24 border-dashed"
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="flex flex-col items-center">
                  <Camera className="h-6 w-6 text-slate-400 mb-1" />
                  <span className="text-sm text-slate-500">Upload Receipt</span>
                </div>
              </Button>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="reimbursement_description">Description *</Label>
            <Input
              id="reimbursement_description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Office supplies, Gas for work trip..."
              required
              data-testid="reimbursement-description-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="reimbursement_amount">Amount *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                <Input
                  id="reimbursement_amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  placeholder="0.00"
                  className="pl-7"
                  required
                  data-testid="reimbursement-amount-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="reimbursement_date">Date *</Label>
              <Input
                id="reimbursement_date"
                type="date"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                required
                data-testid="reimbursement-date-input"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="reimbursement_notes">Notes</Label>
            <Textarea
              id="reimbursement_notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Any additional details..."
              rows={2}
              data-testid="reimbursement-notes-input"
            />
          </div>
          
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-pink-500 hover:bg-pink-600"
              data-testid="save-reimbursement-btn"
            >
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddReimbursementDialog;
